import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { WarningCircle, X } from '@phosphor-icons/react';

import './statusBannerStack.scss';

const MAX_TIMER_DELAY_MS = (2 ** 31) - 1;
const EMPTY_BANNERS = [];
const DISMISSED_STORAGE_KEY = 'statusBanners.dismissed';
const MAX_DISMISSED_KEYS = 20;

// A dismissal hides one version of a notice. The message carries the latest
// incident update, so a new update surfaces the notice again.
const dismissalKey = (banner) => [banner.id, banner.message].join('\n');

const readDismissedKeys = () => {
  try {
    const keys = JSON.parse(window.localStorage.getItem(DISMISSED_STORAGE_KEY));
    return Array.isArray(keys) ? keys.filter((key) => typeof key === 'string') : [];
  } catch {
    return [];
  }
};

const writeDismissedKeys = (keys) => {
  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // Storage can be unavailable; the dismissal still applies to this page.
  }
};

// Parse a wire banner into its visibility window, or null when the shape is
// invalid. This is the single source of truth for "which banners matter" — the
// ingest guard, retention, activation, and the boundary timer all derive from
// it, so those rules cannot drift apart.
const bannerWindow = (banner) => {
  if (!banner || typeof banner !== 'object' || Array.isArray(banner)) return null;
  if (banner.id == null || banner.message == null) return null;

  const startsAt = Date.parse(banner.starts_at);
  if (!Number.isFinite(startsAt)) return null;

  const expiries = [banner.ends_at, banner.stale_at]
    .map((boundary) => Date.parse(boundary))
    .filter(Number.isFinite);
  if (expiries.length === 0) return null;

  return { startsAt, expiresAt: Math.min(...expiries) };
};

const validBanners = (banners) => banners.filter((banner) => bannerWindow(banner) !== null);

// A non-finite priority must never win: NaN comparisons are always false, so a
// malformed priority that became `best` first could otherwise block every real
// notice until its own window expired. Coerce it to the lowest rank instead.
const bannerPriority = (banner) => {
  const priority = Number(banner.priority);
  return Number.isFinite(priority) ? priority : -Infinity;
};

// The single highest-priority notice currently within its window, or null.
// Priority desc, then earliest start, then id — the backend's own ordering.
export const activeStatusBanner = (banners, now = Date.now()) => {
  let best = null;
  let bestWindow = null;

  banners.forEach((banner) => {
    const range = bannerWindow(banner);
    if (!range || range.startsAt > now || range.expiresAt <= now) return;

    const priority = bannerPriority(banner);
    const bestPriority = best ? bannerPriority(best) : -Infinity;
    const wins = !best
      || priority > bestPriority
      || (priority === bestPriority && range.startsAt < bestWindow.startsAt)
      || (priority === bestPriority && range.startsAt === bestWindow.startsAt
        && String(banner.id).localeCompare(String(best.id)) < 0);

    if (wins) {
      best = banner;
      bestWindow = range;
    }
  });

  return best;
};

const StatusBannerStack = ({ authenticated, cable, onHeightChange }) => {
  // Keep the raw channel snapshot plus an explicit clock; the visible notice is
  // a pure derivation of the two, so a boundary crossing is a clock tick rather
  // than a synthetic data update.
  const [state, setState] = useState(() => ({ snapshot: EMPTY_BANNERS, now: Date.now() }));
  const [dismissedKeys, setDismissedKeys] = useState(readDismissedKeys);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!authenticated || !cable) {
      setState((current) => (
        current.snapshot.length === 0 ? current : { snapshot: EMPTY_BANNERS, now: Date.now() }
      ));
      return undefined;
    }

    const subscription = cable.subscriptions.create(
      { channel: 'StatusBannersChannel' },
      {
        received: (payload) => {
          // The channel wraps the subscribe snapshot and every broadcast as
          // { banners: [...] }. A missing/null field or an empty array is an
          // explicit clear; any other non-array value, or a non-empty array with
          // no shape-valid entries, is a malformed frame that must not wipe an
          // active notice.
          if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;

          const { banners: incoming } = payload;
          let nextSnapshot;
          if (incoming == null || (Array.isArray(incoming) && incoming.length === 0)) {
            nextSnapshot = EMPTY_BANNERS;
          } else if (Array.isArray(incoming)) {
            const valid = validBanners(incoming);
            if (valid.length === 0) return;
            nextSnapshot = valid;
          } else {
            return;
          }

          setState({ snapshot: nextSnapshot, now: Date.now() });
        },
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [authenticated, cable]);

  // Advance the clock at the next start/expiry boundary of a retained banner so
  // notices appear and expire on time without another cable message.
  useEffect(() => {
    const { snapshot, now } = state;
    let nextBoundary = Infinity;
    snapshot.forEach((banner) => {
      const range = bannerWindow(banner);
      if (!range || range.expiresAt <= now) return;
      const boundary = range.startsAt > now ? range.startsAt : range.expiresAt;
      if (boundary < nextBoundary) nextBoundary = boundary;
    });
    if (!Number.isFinite(nextBoundary)) return undefined;

    const delay = Math.min(Math.max(nextBoundary - now, 0), MAX_TIMER_DELAY_MS);
    const transitionTimer = window.setTimeout(() => {
      setState((current) => ({ ...current, now: Date.now() }));
    }, delay);

    return () => window.clearTimeout(transitionTimer);
  }, [state]);

  // A dismissed notice yields to the next active one rather than hiding all.
  const undismissed = state.snapshot.filter((banner) => !dismissedKeys.includes(dismissalKey(banner)));
  const activeBanner = activeStatusBanner(undismissed, state.now);
  const visibleKey = activeBanner ? dismissalKey(activeBanner) : '';

  const dismiss = () => {
    const keys = [...dismissedKeys, visibleKey].slice(-MAX_DISMISSED_KEYS);
    writeDismissedKeys(keys);
    setDismissedKeys(keys);
  };

  useLayoutEffect(() => {
    const root = rootRef.current;
    const measure = () => onHeightChange(root ? Math.ceil(root.getBoundingClientRect().height) : 0);

    measure();

    if (!root || !window.ResizeObserver) {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const resizeObserver = new window.ResizeObserver(measure);
    resizeObserver.observe(root);
    return () => resizeObserver.disconnect();
  }, [visibleKey, onHeightChange]);

  // When the notice leaves the tree (a route without the app chrome) report a
  // zero height so the parent stops reserving space for a notice that is gone.
  useEffect(() => () => onHeightChange(0), [onHeightChange]);

  if (!authenticated || !activeBanner) return null;

  return (
    <div
      ref={rootRef}
      className="status-banner-stack"
      data-testid="status-banner-stack"
    >
      <div
        className="status-banner-stack__notice"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <WarningCircle aria-hidden="true" size={18} weight="fill" />
        <span>{activeBanner.message}</span>
      </div>
      <button
        type="button"
        className="status-banner-stack__dismiss"
        onClick={dismiss}
        aria-label="Dismiss status notice"
      >
        <X aria-hidden="true" size={16} weight="bold" />
      </button>
    </div>
  );
};

StatusBannerStack.propTypes = {
  authenticated: PropTypes.bool.isRequired,
  cable: PropTypes.shape({
    subscriptions: PropTypes.shape({
      create: PropTypes.func.isRequired,
    }).isRequired,
  }),
  onHeightChange: PropTypes.func.isRequired,
};

StatusBannerStack.defaultProps = {
  cable: null,
};

export default StatusBannerStack;
